import { useState, useEffect } from 'react';
import MvpHero from './components/MvpHero';
import MvpContexto from './components/MvpContexto';
import MvpTecnica from './components/MvpTecnica';
import MvpOrcamento from './components/MvpOrcamento';
import MvpPagamento from './components/MvpPagamento';
import MvpValorMercado from './components/MvpValorMercado';
import MvpROI from './components/MvpROI';
import MvpEntregaveis from './components/MvpEntregaveis';

const navLinks = [
  { href: '#contexto', label: 'Contexto' },
  { href: '#tecnica', label: 'Proposta Técnica' },
  { href: '#orcamento', label: 'Orçamento' },
  { href: '#pagamento', label: 'Pagamento' },
  { href: '#mercado', label: 'Valor de Mercado' },
  { href: '#roi', label: 'Retorno' },
  { href: '#entregaveis', label: 'Entregáveis' },
  { href: '#resumo', label: 'Resumo Final' },
];

export default function PropostaFase1Page() {
  const [activeSection, setActiveSection] = useState('');
  const [showNav, setShowNav] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setShowNav(window.scrollY > window.innerHeight * 0.8);

      // Calculate scroll progress
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / scrollHeight) * 100;
      setScrollProgress(progress);

      const sections = navLinks.map((l) => l.href.replace('#', ''));
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY + 100 >= el.offsetTop) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href: string) => {
    const id = href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-stone-200">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <MvpHero />

      {/* Floating section nav */}
      <div className={`fixed left-4 top-1/2 -translate-y-1/2 z-40 transition-all duration-500 ${showNav ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none'} hidden xl:block`}>
        <div className="bg-white/90 backdrop-blur border border-stone-200 rounded-2xl p-3">
          <div className="space-y-1">
            {navLinks.map((l) => {
              const id = l.href.replace('#', '');
              return (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className={`block w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                    activeSection === id
                      ? 'bg-[#071210] text-white font-semibold'
                      : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100'
                  }`}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <MvpContexto />
      <MvpTecnica />
      <MvpOrcamento />
      <MvpPagamento />
      <MvpValorMercado />
      <MvpROI />
      <MvpEntregaveis />

      {/* Resumo Final */}
      <section id="resumo" className="py-20 bg-[#071210]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 mb-6">
              <span className="text-emerald-400 text-xs font-medium tracking-wide uppercase">Parte VIII — Resumo Final</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Tabela Consolidada — Fase 1</h2>
            <p className="text-stone-400 text-base">Investimento total de R$ 357.000 e manutenção mensal para a Versão 1 da plataforma Submit.AI.</p>
          </div>

          {/* Version card */}
          <div className="p-8 rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-emerald-600/5 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="text-stone-400 text-sm uppercase tracking-wide mb-2">Fase 1 — Versão 1 — 4 meses</p>
                <div className="flex items-baseline gap-3">
                  <span className="text-white font-bold text-4xl">R$ 357.000</span>
                  <span className="text-stone-500 text-sm">investimento total</span>
                </div>
                <p className="text-emerald-400 text-sm mt-2 font-medium">50% início · 20% meio · 30% entrega</p>
                <p className="text-stone-500 text-xs mt-1">Manutenção pós-entrega: R$ 12.000/mês</p>
              </div>
              <div className="space-y-2.5 w-full sm:w-auto">
                {[
                  { icon: 'ri-radar-line', label: '8 módulos entregues e integrados' },
                  { icon: 'ri-brain-line', label: 'Agente Analisador com 8 categorias extraídas' },
                  { icon: 'ri-quill-pen-line', label: 'Agente Escritor com 10 seções geradas' },
                  { icon: 'ri-shield-check-line', label: 'Checklist automático de conformidade FINEP' },
                  { icon: 'ri-loop-right-line', label: 'Manutenção e suporte pós-entrega incluídos' },
                  { icon: 'ri-calendar-check-line', label: 'Prazo garantido: 4 meses de desenvolvimento' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-md bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-emerald-400 text-xs w-3 h-3 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-stone-300 text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Parcelas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {[
              { label: '1ª Parcela — Dia 0', pct: '50%', valor: 'R$ 178.500', desc: 'Assinatura do contrato', color: 'border-emerald-500/30 bg-emerald-500/10' },
              { label: '2ª Parcela — Dia 60', pct: '20%', valor: 'R$ 71.400', desc: 'Entrega dos Módulos 1 a 4', color: 'border-amber-500/30 bg-amber-500/10' },
              { label: '3ª Parcela — Dia 120', pct: '30%', valor: 'R$ 107.100', desc: 'Go-live e aceite final', color: 'border-white/10 bg-white/5' },
            ].map((p) => (
              <div key={p.label} className={`p-5 rounded-xl border ${p.color}`}>
                <p className="text-stone-400 text-xs mb-1">{p.label}</p>
                <p className="text-white font-bold text-xl mb-0.5">{p.valor}</p>
                <p className="text-emerald-400 text-xs font-semibold">{p.pct}</p>
                <p className="text-stone-500 text-xs mt-1">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Observações finais */}
          <div className="p-5 rounded-xl border border-white/8 bg-white/4">
            <p className="text-stone-300 text-sm text-center">
              <strong className="text-white">Pagamento:</strong> 50% (R$ 178.500) no início, 20% (R$ 71.400) no meio e 30% (R$ 107.100) na entrega final.
              <br />
              <span className="text-stone-500 mx-3">·</span>
              <strong className="text-white">Validade:</strong> 30 dias a partir de 06/04/2026.
              <span className="text-stone-500 mx-3">·</span>
              <strong className="text-white">Escopo:</strong> 100% FINEP.
            </p>
          </div>

          {/* Exclusividade */}
          <div className="mt-8 p-5 rounded-xl border border-amber-500/30 bg-amber-500/10">
            <h4 className="text-amber-400 font-semibold text-sm mb-2">Possibilidade de Exclusividade</h4>
            <p className="text-stone-300 text-sm leading-relaxed">
              Esta proposta contempla a possibilidade de acordo de exclusividade para uso da plataforma Submit.AI no estado do Amazonas. Os termos detalhados — incluindo período de exclusividade, <span className="italic">territorial scope</span> e condições comerciais — serão discutidos e definidos em reunião presencial ou videoconferência. Ficamos abertos para apresentar as opções e encontrar o melhor modelo para o instituto.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">S</span>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Submit.AI</p>
                <p className="text-stone-500 text-xs">Inteligência para captação de recursos via FINEP</p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-stone-500 text-xs">PROPOSTA-SUBMITAI-FASE1 v1.0</p>
              <p className="text-stone-600 text-xs">Manaus, 06 de abril de 2026</p>
            </div>
          </div>

          {/* Disclaimer rodapé */}
          <div className="mt-12 pt-6 border-t border-white/8">
            <p className="text-amber-500/70 text-xs text-center">
              <i className="ri-lock-line mr-1"></i>
              Esta proposta é confidencial. A reprodução ou compartilhamento sem autorização é proibido.
              <span className="mx-2">·</span>
              © 2026 Submit.AI — Todos os direitos reservados.
            </p>
          </div>
        </div>
      </section>

      {/* Floating scroll buttons */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
        <button
          onClick={scrollToTop}
          className="w-12 h-12 rounded-full bg-white border border-stone-200 shadow-lg flex items-center justify-center hover:bg-stone-50 transition-colors"
          aria-label="Voltar ao topo"
        >
          <i className="ri-arrow-up-line text-stone-600"></i>
        </button>
        <button
          onClick={scrollToBottom}
          className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
          aria-label="Ver proposta completa"
        >
          <i className="ri-arrow-down-line text-white"></i>
        </button>
      </div>
    </div>
  );
}
